import type { ReactNode } from 'react';
import {
  Alert,
  Collapse,
  Divider,
  Flex,
  LoadingOverlay,
  ScrollArea,
  Stack,
  Tabs,
  Text,
} from '@mantine/core';
import { TriggerInputDataset } from '@/hooks/api/datasets/trigger/useTriggerManualRun.ts';
import StyledModal from '@/components/StyledModal/StyledModal.tsx';
import StyledSelect from '@/components/StyledInputFields/StyledSelect.tsx';
import StyledTextInput from '@/components/StyledInputFields/StyledTextInput.tsx';
import ModalConfirmationButtons from '@/components/StyledModal/ModalConfirmationButtons.tsx';
import { ManualTriggerInput, StepError, TriggerStep } from '@/types/automationSteps.ts';
import NewRunUI from '@/pages/AutomationPage/components/ManualRunModal/components/NewRunUI.tsx';
import PreviousRunUI from '@/pages/AutomationPage/components/ManualRunModal/components/PreviousRunUI.tsx';
import { Automation, Automations } from '@/types/automations.ts';
import StyledCheckbox from '@/components/StyledInputFields/StyledCheckbox.tsx';
import CacheBypassNotice from '@/components/CacheBypassNotice.tsx';
import { RunWithDatasets } from '@/types/runs.ts';
import { TriggerDatasetCompatibility } from '@/pages/AutomationPage/components/ManualRunModal/utils/triggerDatasetMatching.ts';

export type ManualRunTab = 'new' | 'old';

type props = {
  selectionControls?: ReactNode;
  automations: Automations | undefined;
  selectedAutomationId: string | undefined;
  setSelectedAutomationId: (id: string | undefined) => void;
  opened: boolean;
  onClose: () => void;
  automation?: Automation;
  creatingRun: boolean;
  errors: StepError[];
  triggerStep: TriggerStep | undefined;
  loadingTriggerConfig: boolean;
  formDisabled: boolean;
  runName: string;
  setRunName: (name: string) => void;
  triggers: ManualTriggerInput[];
  triggerDatasets: TriggerInputDataset[];
  updateTriggerDataset: (index: number, updatedDataset: TriggerInputDataset) => void;
  onConfirm: () => void;
  automationId: string | undefined;
  submitDisabled: boolean;
  ignoreCache: boolean;
  setIgnoreCache: (ignoreCache: boolean) => void;
  activeTab: ManualRunTab;
  setActiveTab: (tab: ManualRunTab) => void;
  seedRun?: RunWithDatasets;
  seedCompatibility: TriggerDatasetCompatibility | null;
  onSelectPerfectRun: (run: RunWithDatasets) => void;
  onSelectPartialRun: (run: RunWithDatasets) => void;
};

const getSeedBannerContent = (
  seedRun: RunWithDatasets,
  seedCompatibility: TriggerDatasetCompatibility,
) => {
  if (seedCompatibility.matchType === 'none') {
    return {
      color: 'gray' as const,
      title: 'No compatible inputs were found',
      message: `Run "${seedRun.run.name}" does not share any current trigger inputs with the published version. Fill in the current inputs manually.`,
    };
  }

  if (seedCompatibility.matchType === 'partial') {
    return {
      color: 'yellow' as const,
      title: 'Some inputs were filled from the selected run',
      message: `Filled ${seedCompatibility.matchedTriggerCount} matching input${seedCompatibility.matchedTriggerCount === 1 ? '' : 's'} from "${seedRun.run.name}".`,
    };
  }

  if (seedCompatibility.missingOptionalTriggers.length > 0) {
    return {
      color: 'blue' as const,
      title: 'Required inputs were filled from the selected run',
      message: `All required inputs from "${seedRun.run.name}" were copied into this manual run. Optional inputs that were not used in that run will stay blank.`,
    };
  }

  return {
    color: 'blue' as const,
    title: 'Inputs were filled from the selected run',
    message: `All available matching inputs from "${seedRun.run.name}" were copied into this manual run.`,
  };
};

const ManualRunModalUI = ({
  selectionControls,
  automations,
  selectedAutomationId,
  setSelectedAutomationId,
  opened,
  onClose,
  automation,
  creatingRun,
  errors,
  triggerStep,
  loadingTriggerConfig,
  formDisabled,
  runName,
  setRunName,
  triggers,
  triggerDatasets,
  updateTriggerDataset,
  onConfirm,
  automationId,
  submitDisabled,
  ignoreCache,
  setIgnoreCache,
  activeTab,
  setActiveTab,
  seedRun,
  seedCompatibility,
  onSelectPerfectRun,
  onSelectPartialRun,
}: props) => {
  const versionHasErrors = errors.length > 0;
  const showSeedBanner = !!seedRun && !!seedCompatibility;
  const seedBanner = showSeedBanner ? getSeedBannerContent(seedRun, seedCompatibility) : null;

  return (
    <StyledModal
      opened={opened}
      onClose={onClose}
      title="Create a Manual Run"
      scrollType={'none'}
      size={'lg'}
      zIndex={95}
      closeOnClickOutside={!creatingRun}
      closeOnEscape={!creatingRun}
      withCloseButton={!creatingRun}
    >
      <Stack style={{ position: 'relative' }} gap={'lg'} h={'100%'}>
        <LoadingOverlay visible={creatingRun} zIndex={1000} />

        {!automation && (
          <>
            <StyledSelect
              label="Automation"
              placeholder="Select an existing automation"
              required
              data={automations?.results.map((a) => ({ value: a.id, label: a.name }))}
              value={automations?.results.find((a) => a.id === selectedAutomationId)?.id}
              onChange={(v) => setSelectedAutomationId(v || undefined)}
              allowDeselect={false}
              clearable={false}
            />
            {/*<Divider />*/}
          </>
        )}

        {selectedAutomationId && versionHasErrors && <Text c={'red'}>Automation has errors</Text>}
        {selectionControls}
        {selectedAutomationId && !triggerStep && !loadingTriggerConfig && (
          <Text c={'red'}>Automation has no trigger steps</Text>
        )}

        <Collapse expanded={!formDisabled} mt={'-6px'}>
          <LoadingOverlay visible={loadingTriggerConfig} zIndex={1000} />

          <Flex mx={'-32px'} style={{ overflowX: 'hidden' }}>
            <ScrollArea.Autosize
              w="100%"
              mah="calc(90vh - 150px)"
              type="auto"
              // offsetScrollbars
              scrollbars={'y'}
              styles={{
                viewport: { overflowX: 'hidden' },
                content: { minWidth: 0, width: '100%' },
              }}
            >
              <Tabs
                value={activeTab}
                onChange={(newValue) => {
                  if (newValue === 'new' || newValue === 'old') {
                    setActiveTab(newValue);
                  }
                }}
                color={'5E6AD2'}
                styles={{
                  tab: { borderBottomWidth: '1px' },
                }}
                px={'32px'}
              >
                <Tabs.List style={{ paddingX: '40px' }}>
                  <style>{`.mantine-Tabs-list::before { display: none; }`}</style>
                  <Tabs.Tab value={'new'} c={activeTab === 'new' ? '#5E6AD2' : '#757575'} fw={500}>
                    New run
                  </Tabs.Tab>
                  <Tabs.Tab value={'old'} c={activeTab === 'old' ? '#5E6AD2' : '#757575'} fw={500}>
                    Use previous run
                  </Tabs.Tab>
                </Tabs.List>
                <Divider color={'#E8E8E8'} mt={'-1px'} mx={'-32px'} />

                <Tabs.Panel value="new">
                  <Stack gap={'lg'} mt={'lg'}>
                    {seedBanner && seedCompatibility && (
                      <Alert color={seedBanner.color} title={seedBanner.title}>
                        <Stack gap={4}>
                          <Text size="sm">{seedBanner.message}</Text>
                          {seedCompatibility.matchType !== 'none' &&
                            seedCompatibility.missingRequiredTriggers.length > 0 && (
                              <Text size="sm">
                                Still required:{' '}
                                {seedCompatibility.missingRequiredTriggers
                                  .map((trigger) => trigger.name)
                                  .join(', ')}
                              </Text>
                            )}
                          {seedCompatibility.matchType !== 'none' &&
                            seedCompatibility.extraInputCount > 0 && (
                              <Text size="sm">
                                This older run used {seedCompatibility.extraInputCount} input
                                {seedCompatibility.extraInputCount === 1 ? '' : 's'} that{' '}
                                {seedCompatibility.extraInputCount === 1 ? 'is' : 'are'} not part of
                                the current trigger setup.
                              </Text>
                            )}
                          {seedCompatibility.matchType === 'exact' &&
                            seedCompatibility.missingOptionalTriggers.length > 0 && (
                              <Text size="sm">
                                Optional inputs left blank:{' '}
                                {seedCompatibility.missingOptionalTriggers
                                  .map((trigger) => trigger.name)
                                  .join(', ')}
                              </Text>
                            )}
                        </Stack>
                      </Alert>
                    )}
                    <StyledTextInput
                      label="Run Name"
                      placeholder="Run name"
                      size="sm"
                      value={runName}
                      onChange={(e) => setRunName(e.target.value)}
                      autoFocus={true}
                      data-autofocus
                      required={true}
                      readOnly={creatingRun}
                    />
                    <NewRunUI
                      creatingRun={creatingRun}
                      triggers={triggers}
                      triggerDatasets={triggerDatasets}
                      updateTriggerDataset={updateTriggerDataset}
                      automationId={automationId}
                    />
                  </Stack>
                </Tabs.Panel>

                <Tabs.Panel value="old">
                  <Stack gap={'lg'} mt={'lg'}>
                    {automationId && (
                      <PreviousRunUI
                        triggers={triggers}
                        automationId={automationId}
                        runName={runName}
                        setRunName={setRunName}
                        creatingRun={creatingRun}
                        onSelectPerfectRun={onSelectPerfectRun}
                        onSelectPartialRun={onSelectPartialRun}
                      />
                    )}
                  </Stack>
                </Tabs.Panel>
              </Tabs>
              <Stack px={'32px'} mt={'lg'}>
                <Divider style={{ color: '#ececec', borderTopWidth: '0.5px' }} />
                <StyledCheckbox
                  mx={'xs'}
                  size={'xs'}
                  label="Run without using cached results"
                  checked={ignoreCache}
                  onChange={(event) => setIgnoreCache(event.currentTarget.checked)}
                />
                <CacheBypassNotice enabled={ignoreCache} />
              </Stack>
            </ScrollArea.Autosize>
          </Flex>
        </Collapse>
      </Stack>
      <ModalConfirmationButtons
        onClose={onClose}
        showCancel={false}
        onConfirm={() => onConfirm()}
        confirmText={'Create run'}
        confirmDisabled={submitDisabled}
        confirmLoading={creatingRun}
        closeOnConfirm={false}
      />
    </StyledModal>
  );
};

export default ManualRunModalUI;
